import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { getAnnotation, lintAnnotation, type AnnotationEnvelope } from '@/api/annotations'
import { keys } from '@/api/keys'
import { toast } from '@/components/ui/toast'
import { hasServerLints, isStageBody, normaliseForSave, useEditorStore, type EditorDoc, type SaveError } from '@/stores/editorStore'

/**
 * Saving the stage annotator — **only when the person asks** (§9.1, explicit-Save model).
 *
 * There is no autosave any more: no debounce, no save on a tab switch, on closing the window, on
 * switching simple ↔ expert or on leaving the route. What writes to the server:
 *
 *  - the Save button and Ctrl+S / ⌘S ({@link saveEditor});
 *  - "Save and leave" in the leave dialog, "Save and go back" in the return bar, and the confirm in
 *    front of a publish / status change ({@link ensureSaved});
 *  - "Keep mine" in the conflict dialog.
 *
 * Leaving with unsaved changes is caught by the route's blocker (and the browser's own
 * "leave site?" prompt). A `409` becomes the conflict dialog; `422` is our bug and is logged loudly.
 *
 * The lints (red shapes, the Check tab, the status pill's error count) used to refresh only because
 * every edit was saved. {@link useLiveLint} keeps them fresh with the dry-run lint endpoint instead,
 * without writing anything and without making an undo step.
 */

/** Save what is on screen now. Resolves with the new envelope, or null (nothing to save / failed — see `saveError`). */
export async function saveEditor(qc?: QueryClient): Promise<AnnotationEnvelope | null> {
  const st = useEditorStore.getState()
  if (!st.dirty || st.saving) return null
  const env = await st.save()
  if (env) {
    qc?.setQueryData(keys.annotation.doc(env.asset.uid), env)
  } else {
    const err = useEditorStore.getState().saveError
    // A schema failure means the editor produced something the contract forbids — our bug, noisy.
    if (err?.kind === 'schema') console.error('[annotator] PUT rejected by the JSON schema', err.message)
  }
  return env
}

/** Wait for a save already on its way (at most `ms`). */
export function whenNotSaving(ms = 30_000): Promise<void> {
  if (!useEditorStore.getState().saving) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms)
    const unsub = useEditorStore.subscribe((s) => {
      if (!s.saving) done()
    })
    function done() {
      clearTimeout(timer)
      unsub()
      resolve()
    }
  })
}

/**
 * Make sure everything on screen is saved: waits for a save on its way, then saves what is still
 * unsaved. Resolves true when the document is clean afterwards; false when saving failed (the
 * reason is in `saveError`).
 */
export async function ensureSaved(qc?: QueryClient): Promise<boolean> {
  await whenNotSaving()
  if (!useEditorStore.getState().dirty) return true
  await saveEditor(qc)
  const st = useEditorStore.getState()
  return !st.dirty && !st.saveError
}

/** What a save failure means, in words a parent can act on. */
export function describeSaveError(err: SaveError | null): string | null {
  if (!err) return null
  switch (err.kind) {
    case 'conflict':
      return 'Someone else saved a newer version of this picture.'
    case 'network':
      return 'The studio could not be reached. Your changes are still here — try again.'
    case 'schema':
      return 'The studio could not save this picture (a problem on our side). Your changes are still here.'
    default:
      return err.message || 'Saving did not work. Your changes are still here — try again.'
  }
}

export interface SaveController {
  /** Save now (the Save button, Ctrl+S). */
  save(): Promise<AnnotationEnvelope | null>
  /** Old name of {@link save}. */
  flush(): Promise<void>
  saving: boolean
  dirty: boolean
  savedAt: string | null
  error: SaveError | null
  /** Set while a `409` is unresolved. */
  conflict: { current_rev: number } | null
  /** Go back to the last saved version (undoable). */
  discard(): boolean
  /** Take the newer version someone else saved (undoable: Ctrl+Z brings yours back). */
  reloadTheirs(): Promise<void>
  /** Save what is on screen on top of the newer version. */
  keepMine(): Promise<void>
}

export function useSaveController(): SaveController {
  const qc = useQueryClient()
  const dirty = useEditorStore((s) => s.dirty)
  const saving = useEditorStore((s) => s.saving)
  const savedAt = useEditorStore((s) => s.savedAt)
  const error = useEditorStore((s) => s.saveError)
  const [resolving, setResolving] = useState(false)

  const conflict = error?.kind === 'conflict' ? { current_rev: error.current_rev } : null

  const save = useCallback(() => saveEditor(qc), [qc])
  const flush = useCallback(async () => {
    await saveEditor(qc)
  }, [qc])

  const discard = useCallback(() => useEditorStore.getState().discard(), [])

  const reloadTheirs = useCallback(async () => {
    const meta = useEditorStore.getState().meta
    if (!meta) return
    setResolving(true)
    try {
      const env = await getAnnotation(meta.uid)
      useEditorStore.getState().adoptServerVersion(env)
      qc.setQueryData(keys.annotation.doc(meta.uid), env)
    } catch (err) {
      // The conflict stays open (nothing changed on screen); say why instead of failing silently.
      toast.error('Could not fetch the newer version', { description: err instanceof Error ? err.message : 'Check the connection and try again.' })
    } finally {
      setResolving(false)
    }
  }, [qc])

  const keepMine = useCallback(async () => {
    setResolving(true)
    try {
      const env = await useEditorStore.getState().keepMine()
      if (env) qc.setQueryData(keys.annotation.doc(env.asset.uid), env)
    } finally {
      setResolving(false)
    }
  }, [qc])

  return { save, flush, saving: saving || resolving, dirty, savedAt, error, conflict, discard, reloadTheirs, keepMine }
}

/**
 * @deprecated There is no autosave any more; this is {@link useSaveController} under its old name
 * so a shell that still calls it keeps compiling. The argument is ignored.
 */
export function useAutosave(_enabled = true): SaveController {
  return useSaveController()
}

/* -------------------------------------------------------------------------- */
/* Live lints                                                                  */
/* -------------------------------------------------------------------------- */

export const LIVE_LINT_DEBOUNCE_MS = 600

/**
 * Keep `doc.lints` fresh while editing: {@link LIVE_LINT_DEBOUNCE_MS} after the document stops
 * changing, POST it to the dry-run lint endpoint and swap the answer in — only if the document is
 * still the same object. Writes nothing on the server and never makes an undo step. Mount once
 * per open editor (the annotate route does).
 */
export function useLiveLint(enabled = true): void {
  useEffect(() => {
    if (!enabled) return
    let timer: ReturnType<typeof setTimeout> | null = null
    let ctrl: AbortController | null = null

    const run = async (doc: EditorDoc) => {
      const st = useEditorStore.getState()
      if (st.doc !== doc || !st.meta || st.gesturing || st.saving || hasServerLints(doc)) return
      ctrl?.abort()
      const mine = new AbortController()
      ctrl = mine
      try {
        const res = await lintAnnotation(st.meta.uid, normaliseForSave(doc), mine.signal)
        if (mine.signal.aborted) return
        useEditorStore.getState().applyServerLints(doc, res.lints, res.affordances)
      } catch {
        // Offline or refused: the lints on screen stay as they were; the next save refreshes them.
      }
    }

    const schedule = (doc: EditorDoc | null) => {
      if (timer) clearTimeout(timer)
      timer = null
      if (!doc || !isStageBody(doc) || hasServerLints(doc)) return
      timer = setTimeout(() => void run(doc), LIVE_LINT_DEBOUNCE_MS)
    }

    const unsub = useEditorStore.subscribe((s, prev) => {
      if (s.doc !== prev.doc || (prev.saving && !s.saving) || (prev.gesturing && !s.gesturing)) schedule(s.doc)
    })
    schedule(useEditorStore.getState().doc)
    return () => {
      unsub()
      if (timer) clearTimeout(timer)
      ctrl?.abort()
    }
  }, [enabled])
}
